import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAuthModal } from '../context/AuthModalContext';
import { useCart } from '../context/CartContext';
import AuthModals from './AuthModals';
import { FiMenu, FiX, FiUser, FiLogOut, FiHome, FiDollarSign, FiInfo, FiMail, FiShoppingCart } from 'react-icons/fi';
import { BsGraphUp } from 'react-icons/bs';
import logo from '../assets/logo.svg';
import { toast } from 'react-toastify';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const { modalType, openModal: openAuthModal, closeModal: closeAuthModal } = useAuthModal();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Close mobile menu when route changes
    setIsOpen(false);
    
    // Handle scroll effect for navbar
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location]);

  useEffect(() => {
    // Redirect to dashboard if user is authenticated and on login/signup
    if (user && ['/login', '/signup'].includes(location.pathname)) {
      navigate('/dashboard');
    }
  }, [user, navigate, location]);

  const openModal = (type) => {
    openAuthModal(type);
    setIsOpen(false);
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleScroll = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsOpen(false);
  };

  const navItems = [
    { id: 'home', label: 'Home', icon: <FiHome />, action: () => navigate('/') },
    { id: 'services', label: 'Services', icon: <FiDollarSign />, action: () => handleScroll('plans') },
    { id: 'about', label: 'About', icon: <FiInfo />, action: () => navigate('/about') },
    { id: 'contact', label: 'Contact', icon: <FiMail />, action: () => handleScroll('contact') },
    ...(user ? [{ id: 'dashboard', label: 'Dashboard', icon: <BsGraphUp />, action: () => navigate('/dashboard') }] : [])
  ];

  return (
    <>
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md py-2' : 'bg-white py-4'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <img src={logo} alt="Eunbro Logo" className="h-8 w-auto" />
                <span className="ml-2 text-xl font-bold text-indigo-600 hidden sm:inline">Eunbro</span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={item.action}
                  className={`px-3 py-2 rounded-md text-sm font-medium flex items-center ${
                    location.pathname === '/' && window.location.hash === `#${item.id}`
                      ? 'text-indigo-600 bg-indigo-50'
                      : 'text-gray-700 hover:text-indigo-600 hover:bg-indigo-50'
                  }`}
                >
                  <span className="mr-2">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>

            {/* Cart + Auth Buttons */}
            <div className="hidden md:flex items-center space-x-4 ml-4">
              <Link
                to="/cart"
                aria-label={`Cart, ${itemCount} item${itemCount === 1 ? '' : 's'}`}
                className="relative p-2 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
              >
                <FiShoppingCart className="w-5 h-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center h-4 w-4 min-w-[1rem] px-1 bg-indigo-600 text-white text-[10px] font-bold rounded-full">
                    {itemCount}
                  </span>
                )}
              </Link>
              {user ? (
                <div className="relative group">
                  <button className="flex items-center space-x-2 text-gray-700 hover:text-indigo-600">
                    <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                      <FiUser className="text-indigo-600" />
                    </div>
                    <span className="text-sm font-medium">{user.email.split('@')[0]}</span>
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 invisible group-hover:visible">
                    <button
                      onClick={handleLogout}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 w-full text-left"
                    >
                      <FiLogOut className="mr-2" />
                      Logout
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => openModal('login')}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-indigo-600"
                  >
                    Log In
                  </button>
                  <button
                    onClick={() => openModal('signup')}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors"
                  >
                    Sign Up
                  </button>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center gap-1">
              <Link
                to="/cart"
                aria-label={`Cart, ${itemCount} item${itemCount === 1 ? '' : 's'}`}
                className="relative p-2 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
              >
                <FiShoppingCart className="w-5 h-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center h-4 w-4 min-w-[1rem] px-1 bg-indigo-600 text-white text-[10px] font-bold rounded-full">
                    {itemCount}
                  </span>
                )}
              </Link>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 focus:outline-none"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                {isOpen ? <FiX className="block h-6 w-6" /> : <FiMenu className="block h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`${isOpen ? 'block' : 'hidden'} md:hidden bg-white shadow-xl`}>
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={item.action}
                className={`w-full flex items-center px-3 py-2 rounded-md text-base font-medium ${
                  location.pathname === '/' && window.location.hash === `#${item.id}`
                    ? 'text-indigo-600 bg-indigo-50'
                    : 'text-gray-700 hover:text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                <span className="mr-2">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>
          <div className="pt-4 pb-3 border-t border-gray-200">
            {user ? (
              <div className="flex items-center px-5">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <FiUser className="text-indigo-600" />
                  </div>
                </div>
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-700">{user.email}</div>
                </div>
              </div>
            ) : (
              <div className="px-2 space-y-1">
                <button
                  onClick={() => openModal('login')}
                  className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-md"
                >
                  Log In
                </button>
                <button
                  onClick={() => openModal('signup')}
                  className="w-full flex items-center justify-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
                >
                  Sign Up
                </button>
              </div>
            )}
            {user && (
              <div className="mt-3 px-2">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-md"
                >
                  <FiLogOut className="mr-2" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Spacer to account for fixed navbar */}
      <div className="h-16"></div>

      <AuthModals
        isOpen={modalType === 'signup'}
        onClose={closeAuthModal}
        type="signup"
      />
      <AuthModals
        isOpen={modalType === 'login'}
        onClose={closeAuthModal}
        type="login"
      />
    </>
  );
};

export default Navbar;